import { Box, Typography } from '@mui/material';
import { useCallback, useState, type ReactElement } from 'react';

import type { AdminApi, AdminUserBaby } from '@/admin/adminApi';
import { actionLabel, describeActor, describeAuditPage, formatCompact, reasonOf } from '@/admin/audit/auditPresentation';
import { AUDIT_PATH } from '@/admin/audit/auditRoutes';
import { FAMILIES_PATH } from '@/admin/families/routes';
import { ReadBlock } from '@/admin/pilotage/ReadBlock';
import { useAdminRead } from '@/admin/pilotage/useAdminRead';
import { Avatar, Badge, Button, EmptyState } from '@/ui/kit';

import { babyName, formatInstant } from './accountPresentation';
import { TechnicalId } from './accountsUi';

/**
 * Onglets « Familles et bebes » et « Journal du compte ».
 *
 * Le journal est celui de l'**administration** sur ce compte : les actions
 * sensibles qu'un operateur y a exercees (`admin.user.*`), avec leur motif. Il
 * ne dit rien de ce que la personne a fait dans l'application.
 */

const PER_PAGE = 20;

/**
 * Profils bebe crees par le compte, d'apres le journal de synchronisation
 * (ADR 0035). Le serveur ne rend ni role ni coparents : un profil rejoint par
 * invitation n'est pas liste, et l'ecran le dit au lieu de laisser croire que la
 * liste est exhaustive.
 */
export const BabiesPanel = ({
  babies,
  anonymized,
}: {
  babies: readonly AdminUserBaby[];
  anonymized: boolean;
}): ReactElement => {
  if (anonymized) {
    return (
      <EmptyState
        title="Aucun profil affiché"
        description="Compte anonymisé : le serveur n’expose plus les profils bébé que ce compte avait créés."
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
        Profils créés par ce compte, d’après les événements de synchronisation reçus par le serveur. Un profil que cette
        personne a rejoint par invitation n’apparaît pas ici.
      </Typography>
      {babies.length === 0 ? (
        <Typography sx={{ fontSize: 14 }}>Ce compte n’a créé aucun profil bébé.</Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {babies.map((baby) => (
            <Box
              key={baby.id}
              component="li"
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                border: 1,
                borderColor: 'tokens.border',
                borderRadius: '14px',
              }}
            >
              <Avatar name={babyName(baby)} tone="blue" size={40} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{babyName(baby)}</Typography>
                <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                  Créé le {formatInstant(baby.createdAt)} (date de l’appareil)
                </Typography>
                <TechnicalId value={baby.id} label="l’identifiant du profil" />
              </Box>
              <Button
                size="S"
                variant="ghost"
                to={`${FAMILIES_PATH}/${encodeURIComponent(baby.id)}`}
                aria-label={`Voir la famille de ${babyName(baby)}`}
              >
                Voir la famille
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

/** « Journal du compte » : `GET /admin/audit-events?targetType=identity_user&targetId=…` (BO-08). */
export const JournalPanel = ({ userId, refreshToken }: { userId: string; refreshToken: number }): ReactElement => {
  const [page, setPage] = useState(1);
  const read = useCallback(
    (api: AdminApi) =>
      api.listAuditEvents({ targetType: 'identity_user', targetId: userId }, page, PER_PAGE),
    [userId, page],
  );
  const { state, reload } = useAdminRead(read, refreshToken);

  return (
    <ReadBlock state={state} label="Journal du compte" onRetry={reload} skeletonHeight={160}>
      {(result) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
            Les actions d’administration exercées sur ce compte : qui, quand, et pourquoi. Les simples consultations
            ne sont pas listées ici.
          </Typography>

          {result.items.length === 0 ? (
            <Typography sx={{ fontSize: 14 }}>Aucune action d’administration n’a été exercée sur ce compte.</Typography>
          ) : (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column' }}>
              {result.items.map((event) => {
                const actor = describeActor(event);
                const reason = reasonOf(event.metadata);

                return (
                  <Box
                    key={event.id}
                    component="li"
                    sx={{ py: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5, '& + &': { borderTop: 1, borderColor: 'tokens.border' } }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Badge label={actionLabel(event.action)} tone="neutral" />
                      <Typography component="time" dateTime={event.occurredAt} sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                        {formatCompact(event.occurredAt) ?? event.occurredAt}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13 }}>Par {actor.label ?? '—'}</Typography>
                    {reason === null ? null : (
                      <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>Motif : {reason}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Button
              size="S"
              onClick={() => {
                setPage((current) => Math.max(1, current - 1));
              }}
              disabled={result.page <= 1}
            >
              Page précédente
            </Button>
            <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              {describeAuditPage({
                total: result.total,
                page: result.page,
                perPage: result.perPage,
                itemCount: result.items.length,
              })}
            </Typography>
            <Button
              size="S"
              onClick={() => {
                setPage((current) => current + 1);
              }}
              disabled={result.page * result.perPage >= result.total}
            >
              Page suivante
            </Button>
          </Box>

          <Box>
            <Button
              size="S"
              variant="ghost"
              to={`${AUDIT_PATH}?period=all&targetType=identity_user&targetId=${encodeURIComponent(userId)}`}
            >
              Ouvrir dans le journal d’audit
            </Button>
          </Box>
        </Box>
      )}
    </ReadBlock>
  );
};
